/**
 * PropertySection — one full property card (info column + sidebar). Every offer
 * renders its own section so all results live on a single scrollable page; the
 * offer tabs scroll to these via the forwarded `ref` and the `data-index` attr.
 *
 * Props:
 *   - offer            {Object}
 *   - index            {number}            — position in the list (used for the anchor id)
 *   - onViewFacilities {()=>void}
 *   - ref              forwarded to the <section> so the parent can scroll to it
 */
import React, { useState } from 'react';
import PropertyInfo from './PropertyInfo';
import Sidebar from './Sidebar';
import AvailableUnits from './AvailableUnits';
import LocationMap from './LocationMap';
import PurchaseCosts from './PurchaseCosts';

const PropertySection = React.forwardRef(function PropertySection(
    { offer, index, onViewFacilities, settings = {}, sectionFormat = 'standard', mode, onOpen },
    ref,
) {
    // Per-agency section visibility (default shown when the setting is absent).
    // A selected-unit offer IS one unit — the development price list doesn't apply.
    const showUnits = settings.showUnits !== false && !offer.isUnitOffer;
    const showMap = settings.showMap !== false;
    const showFeatures = settings.showFeatures !== false;
    // Secondary "compact" section format (desktop only — see ResalesLanding):
    // a contained carousel with a top margin, and the property details pulled up
    // to the top of the section.
    const compact = sectionFormat === 'compact';
    // Grid layout modes (mobile): 'nav' — a results card that stays collapsed and
    // whose View Details button OPENS the detail page (onOpen) instead of
    // expanding in place; 'detail' — the detail page itself, always expanded with
    // the toggle hidden. Default (undefined) keeps the in-place expand behaviour.
    const nav = mode === 'nav';
    const detail = mode === 'detail';
    // Mobile-only: the card starts collapsed to just the gallery/price/title,
    // with a button (rendered under the title in PropertyInfo) to reveal the
    // rest — features, property details, certificates, description, map and
    // buying costs. On desktop the `--collapsed` class is a no-op (see SCSS).
    const [expanded, setExpanded] = useState(false);
    const isExpanded = detail ? true : (nav ? false : expanded);

    return (
        <section
            ref={ref}
            id={`rs-offer-${index}`}
            data-index={index}
            className={`rs-property-card${compact ? ' rs-property-card--compact' : ''}${isExpanded ? '' : ' rs-property-card--collapsed'}`}
        >
            <div className="rs-grid">
                <PropertyInfo
                    offer={offer}
                    onViewFacilities={onViewFacilities}
                    detailsExpanded={isExpanded}
                    onToggleExpand={nav ? onOpen : () => setExpanded((v) => !v)}
                    showFeatures={showFeatures}
                    compact={compact}
                    navArrow={nav}
                    hideToggle={detail}
                />
                <Sidebar offer={offer} />
                {/* Card "tail": on desktop it sits under the main info (left
                    column); on mobile/tablet it stacks after the right column,
                    so the offer flows features -> details/energy -> map. */}
                <div className="rs-grid__tail">
                    {showUnits && <AvailableUnits offer={offer} />}
                    {showMap && <LocationMap offer={offer} />}
                    <PurchaseCosts offer={offer} />
                </div>
            </div>
        </section>
    );
});

export default PropertySection;
